import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { HERO_IMAGE, SITE } from "@/constants/site";

const LINES = [
  { text: "LA FORCE", accent: false },
  { text: "DU CHANTIER", accent: false },
  { text: "AU CŒUR DE", accent: false },
  { text: "L'AMAZONIE.", accent: true },
];

const METRICS = [
  { label: `SIREN ${SITE.siren}`, mono: true },
  { label: `Depuis ${SITE.founded}` },
  { label: "Maripasoula · Fleuve Maroni" },
  { label: "5 pôles de services" },
];

export const HeroSection = ({ onQuote }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative min-h-screen flex flex-col justify-end overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
        <img
          src={HERO_IMAGE}
          alt="Pelle mécanique en action sur un chantier de terrassement en Guyane"
          className="w-full h-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-[#0D1117] via-[#0D1117]/70 to-[#0D1117]/30" />
      <div className="absolute inset-0 noise-bg pointer-events-none" />

      <motion.div style={{ opacity: fade }} className="relative z-10 max-w-7xl mx-auto w-full px-5 sm:px-8 pb-10 pt-40">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.1, duration: 0.8 }}
          className="font-mono text-xs sm:text-sm tracking-[0.35em] uppercase text-[#E5A93C] mb-6"
          data-testid="hero-eyebrow"
        >
          Maripasoula — Guyane française
        </motion.p>

        <h1 className="font-display font-extrabold uppercase tracking-tight leading-[0.95] text-4xl sm:text-5xl lg:text-7xl max-w-4xl" data-testid="hero-title">
          {LINES.map((line, i) => (
            <span key={line.text} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${line.accent ? "text-[#F2541B]" : "text-[#F0F6FC]"}`}
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{ delay: 0.35 + i * 0.13, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              >
                {line.text}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.8 }}
          className="mt-6 max-w-xl text-base sm:text-lg text-[#8B949E] font-light leading-relaxed"
          data-testid="hero-subtitle"
        >
          Terrassement, location d'engins & de véhicules, appartements meublés,
          matériaux BTP et transport fluvial — une seule équipe pour vos projets
          au cœur de la Guyane.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.35, duration: 0.8 }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <button
            onClick={() => onQuote()}
            data-testid="hero-devis-btn"
            className="group bg-[#F2541B] hover:bg-[#D9420B] text-white font-display font-bold uppercase tracking-wide text-base px-8 py-4 rounded-full transition-colors duration-300 flex items-center gap-2"
          >
            Demander un devis
            <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
          <a
            href="#catalogue"
            data-testid="hero-catalogue-btn"
            className="border border-[#8B949E]/40 hover:border-[#F0F6FC] text-[#F0F6FC] font-display font-bold uppercase tracking-wide text-base px-8 py-4 rounded-full transition-colors duration-300"
          >
            Voir le catalogue
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 1 }}
          className="mt-14 pt-6 border-t border-[#F0F6FC]/10 grid grid-cols-2 lg:grid-cols-4 gap-4"
          data-testid="hero-metrics"
        >
          {METRICS.map((m) => (
            <span key={m.label} className={`text-xs sm:text-sm text-[#8B949E] ${m.mono ? "font-mono tracking-wider" : ""}`}>
              {m.label}
            </span>
          ))}
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-8 right-8 hidden lg:flex text-[#8B949E]"
      >
        <ArrowDown size={20} className="animate-bounce" />
      </motion.div>
    </section>
  );
};
